import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function DeployExploitInterface() {
  const navigate = useNavigate();
  const [selectedExploit, setSelectedExploit] = useState('EternalBlue (SMB)');
  const [targetIP, setTargetIP] = useState('192.168.1.10');
  const [exploitLog, setExploitLog] = useState([]);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployProgress, setDeployProgress] = useState(0);

  const availableExploits = [
    { name: 'EternalBlue (SMB)', cve: 'CVE-2017-0144' },
    { name: 'Shellshock (CGI)', cve: 'CVE-2014-6271' },
    { name: 'Heartbleed (SSL)', cve: 'CVE-2014-0160' },
    { name: 'Log4Shell (JNDI)', cve: 'CVE-2021-44228' },
    { name: 'BlueKeep (RDP)', cve: 'CVE-2019-0708' },
    { name: 'Zero-Day Buffer (Mem)', cve: 'ZDAY-2025-001' },
  ];

  const deployExploit = () => {
    setIsDeploying(true);
    setExploitLog([]);
    setDeployProgress(0);
    let currentStep = 0;

    const logSteps = [
      `[INIT] Loading exploit module: ${selectedExploit} for ${targetIP}...`,
      `[STEP 1/5] Initiating target handshake and fingerprinting...`,
      `[STEP 2/5] Analyzing target vulnerability profile...`,
      `[STEP 3/5] Compiling and obfuscating payload for ${selectedExploit}...`,
      `[STEP 4/5] Establishing covert channel to ${targetIP}:4444...`,
      `[STEP 5/5] Injecting payload and triggering exploit...`,
      `[STATUS] Awaiting remote shell response...`,
      `[SUCCESS] Remote shell established on ${targetIP}! Session ID: ${Math.floor(1000 + Math.random() * 9000)}.`,
      `shell@${targetIP}:/# whoami`,
      `root`,
      `shell@${targetIP}:/# ls -la /`,
      `[INFO] Target compromised. Access granted.`,
      `glocybs@console:~$ `
    ];

    const deployInterval = setInterval(() => {
      if (currentStep < logSteps.length) {
        setExploitLog(prev => [...prev, logSteps[currentStep]]);
        setDeployProgress(Math.floor((currentStep / logSteps.length) * 100));
        currentStep++;
      } else {
        clearInterval(deployInterval);
        setIsDeploying(false);
        setDeployProgress(100);
      }
    }, 400); // Simulate deployment speed

    return () => clearInterval(deployInterval);
  };

  const commonStyles = { // Re-using common styles
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a0a1a 0%, #1a0a2a 50%, #0a0a1a 100%)',
      fontFamily: "'Inter', sans-serif",
      color: '#00e6e6',
      overflowX: 'hidden',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      padding: '10px',
      boxSizing: 'border-box',
    },
    '@keyframes subtleGridMove': {
      '0%': { backgroundPosition: '0 0' },
      '100%': { backgroundPosition: '100% 100%' },
    },
    '::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: `
        linear-gradient(to right, rgba(0, 230, 230, 0.05) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(0, 230, 230, 0.05) 1px, transparent 1px)
      `,
      backgroundSize: '20px 20px',
      animation: 'subtleGridMove 60s linear infinite',
      opacity: 0.2,
      zIndex: 0,
    },
    headerBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: 'rgba(10, 10, 26, 0.95)',
      padding: '15px 30px',
      borderRadius: '8px',
      marginBottom: '15px',
      border: '1px solid rgba(0, 230, 230, 0.3)',
      boxShadow: '0 0 20px rgba(0, 230, 230, 0.2)',
      zIndex: 5,
      flexWrap: 'wrap',
      gap: '10px',
      '@media (max-width: 768px)': {
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: '10px 15px',
      },
    },
    headerSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      '@media (max-width: 768px)': {
        width: '100%',
        justifyContent: 'space-between',
        marginBottom: '5px',
      },
    },
    headerLabel: {
      fontSize: '11px',
      color: '#888',
      textTransform: 'uppercase',
      letterSpacing: '0.8px',
      '@media (max-width: 480px)': {
        fontSize: '10px',
      },
    },
    headerValue: {
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#00e6e6',
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      '@media (max-width: 480px)': {
        fontSize: '12px',
      },
    },
    progressBar: {
      width: '60px',
      height: '4px',
      background: 'rgba(0, 230, 230, 0.2)',
      borderRadius: '2px',
      overflow: 'hidden',
      display: 'block',
      border: '1px solid rgba(0, 230, 230, 0.4)',
    },
    progressFill: {
      height: '100%',
      background: 'linear-gradient(90deg, #ff6b6b, #e03b4d)', // Exploit specific color
      transition: 'width 0.1s ease',
      boxShadow: '0 0 6px #ff6b6b',
    },
    mainContent: {
      display: 'grid',
      gridTemplateColumns: '1fr 2fr', // Two columns for this page
      gap: '20px',
      flex: 1,
      zIndex: 2,
      '@media (max-width: 1024px)': { // Adjust breakpoint for this page
        gridTemplateColumns: '1fr', // Single column for smaller screens
      },
    },
    panel: {
      background: 'rgba(10, 10, 26, 0.8)',
      border: '1px solid rgba(0, 230, 230, 0.2)',
      borderRadius: '8px',
      padding: '20px',
      boxShadow: '0 0 15px rgba(0, 230, 230, 0.1)',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minHeight: '200px',
      '@media (max-width: 480px)': {
        padding: '15px',
      },
    },
    panelHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '15px',
      fontWeight: 'bold',
      color: '#ff6b6b', // Exploit specific header color
      marginBottom: '15px',
      letterSpacing: '0.8px',
      borderBottom: '1px dashed rgba(255, 107, 107, 0.2)',
      paddingBottom: '10px',
      '@media (max-width: 480px)': {
        fontSize: '13px',
        marginBottom: '10px',
      },
    },
    panelIcon: {
      fontSize: '20px',
      lineHeight: 1,
      '@media (max-width: 480px)': {
        fontSize: '16px',
      },
    },
    formGroup: {
      marginBottom: '15px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    label: {
      fontSize: '12px',
      color: '#888',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    input: {
      width: 'calc(100% - 20px)',
      padding: '10px',
      background: 'rgba(0, 0, 0, 0.4)',
      border: '1px solid rgba(255, 107, 107, 0.4)',
      borderRadius: '5px',
      color: '#ff6b6b',
      fontSize: '14px',
      fontFamily: "'Courier New', monospace",
      '&:focus': {
        outline: 'none',
        borderColor: '#ff6b6b',
        boxShadow: '0 0 8px rgba(255, 107, 107, 0.5)',
      },
      '@media (max-width: 480px)': {
        fontSize: '12px',
      },
    },
    select: {
      width: '100%',
      padding: '10px',
      background: 'rgba(0, 0, 0, 0.4)',
      border: '1px solid rgba(255, 107, 107, 0.4)',
      borderRadius: '5px',
      color: '#ff6b6b',
      fontSize: '14px',
      fontFamily: "'Courier New', monospace",
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath fill='%23FF6B6B' d='M9.293 12.95l-3.536-3.536.707-.707L10 11.536l3.536-3.536.707.707z'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 10px center',
      backgroundSize: '12px',
      '&:focus': {
        outline: 'none',
        borderColor: '#ff6b6b',
        boxShadow: '0 0 8px rgba(255, 107, 107, 0.5)',
      },
      '@media (max-width: 480px)': {
        fontSize: '12px',
      },
    },
    actionButton: {
      background: 'linear-gradient(45deg, #ff6b6b, #e03b4d)', // Exploit specific button color
      color: '#0a0a1a',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 25px',
      fontSize: '15px',
      fontWeight: 'bold',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      transition: 'all 0.3s ease',
      boxShadow: '0 0 10px rgba(255, 107, 107, 0.4)',
      justifyContent: 'center',
      width: 'fit-content',
      margin: '20px auto 0',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: '0 5px 20px rgba(255, 107, 107, 0.6)',
      },
      '&:disabled': {
        background: 'linear-gradient(45deg, #555, #333)',
        cursor: 'not-allowed',
        boxShadow: 'none',
        transform: 'none',
        color: '#888',
      },
      '@media (max-width: 480px)': {
        width: '100%',
        padding: '10px 15px',
        fontSize: '13px',
      },
    },
    terminalOutput: {
      background: 'rgba(0, 0, 0, 0.6)',
      border: '1px solid rgba(255, 107, 107, 0.3)',
      borderRadius: '5px',
      padding: '15px',
      flex: 1,
      overflowY: 'scroll',
      scrollbarWidth: 'thin',
      scrollbarColor: '#ff6b6b #0a0a1a',
      lineHeight: '1.5',
      fontSize: '11px',
      whiteSpace: 'pre-wrap',
      fontFamily: "'Courier New', monospace",
      '&::-webkit-scrollbar': { width: '6px' },
      '&::-webkit-scrollbar-track': { background: '#0a0a1a', borderRadius: '3px' },
      '&::-webkit-scrollbar-thumb': { background: '#ff6b6b', borderRadius: '3px' },
      '@media (max-width: 480px)': {
        fontSize: '9px',
        padding: '10px',
      },
    },
    terminalLine: {
      marginBottom: '2px',
      color: '#ff6b6b',
      textShadow: '0 0 2px rgba(255, 107, 107, 0.5)',
    },
    // Back button
    backButton: {
      background: 'linear-gradient(45deg, #444, #222)',
      color: '#ff6b6b',
      border: '1px solid #ff6b6b',
      borderRadius: '5px',
      padding: '8px 15px',
      fontSize: '12px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      position: 'absolute',
      top: '15px',
      left: '15px',
      zIndex: 10,
      '&:hover': {
        background: 'linear-gradient(45deg, #ff6b6b, #e03b4d)',
        color: '#0a0a1a',
        boxShadow: '0 0 10px rgba(255, 107, 107, 0.6)',
      },
      '@media (max-width: 480px)': {
        padding: '6px 10px',
        fontSize: '10px',
        top: '10px',
        left: '10px',
      },
    },
  };

  return (
    <div style={commonStyles.container}>
      <button style={commonStyles.backButton} onClick={() => navigate('/')}>
        ← Back to Main
      </button>

      <div style={commonStyles.headerBar}>
        <div style={commonStyles.headerSection}>
          <span style={commonStyles.headerLabel}>MODULE:</span>
          <span style={commonStyles.headerValue}>EXPLOIT DEPLOYMENT</span>
        </div>
        <div style={commonStyles.headerSection}>
          <span style={commonStyles.headerLabel}>STATUS:</span>
          <span style={commonStyles.headerValue}>
            {isDeploying ? 'DEPLOYING...' : 'IDLE'}
          </span>
        </div>
        <div style={commonStyles.headerSection}>
          <span style={commonStyles.headerLabel}>PROGRESS:</span>
          <span style={commonStyles.headerValue}>
            <span style={commonStyles.progressBar}>
              <div style={{...commonStyles.progressFill, width: `${deployProgress}%`}}></div>
            </span>
            {deployProgress}%
          </span>
        </div>
      </div>

      <div style={commonStyles.mainContent}>
        {/* Left Panel: Exploit & Target Selection */}
        <div style={commonStyles.panel}>
          <div style={commonStyles.panelHeader}>
            <span style={commonStyles.panelIcon}>🎯</span> EXPLOIT & TARGET SELECTION
          </div>
          <div style={commonStyles.formGroup}>
            <label style={commonStyles.label} htmlFor="exploitSelect">Select Exploit:</label>
            <select
              style={commonStyles.select}
              id="exploitSelect"
              value={selectedExploit}
              onChange={(e) => setSelectedExploit(e.target.value)}
              disabled={isDeploying}
            >
              {availableExploits.map((exploit, index) => (
                <option key={index} value={exploit.name}>
                  {exploit.name} ({exploit.cve})
                </option>
              ))}
            </select>
          </div>
          <div style={commonStyles.formGroup}>
            <label style={commonStyles.label} htmlFor="targetIp">Target IP Address:</label>
            <input
              style={commonStyles.input}
              type="text"
              id="targetIp"
              value={targetIP}
              onChange={(e) => setTargetIP(e.target.value)}
              placeholder="e.g., 192.168.1.10"
              disabled={isDeploying}
            />
          </div>
          <button
            style={commonStyles.actionButton}
            onClick={deployExploit}
            disabled={isDeploying}
          >
            <span style={commonStyles.panelIcon}>🔥</span> {isDeploying ? 'DEPLOYING...' : 'DEPLOY EXPLOIT'}
          </button>
        </div>

        {/* Right Panel: Deployment Log */}
        <div style={commonStyles.panel}>
          <div style={commonStyles.panelHeader}>
            <span style={commonStyles.panelIcon}>📜</span> DEPLOYMENT LOG
          </div>
          <div style={commonStyles.terminalOutput}>
            {exploitLog.map((line, index) => (
              <div key={index} style={commonStyles.terminalLine}>
                {line}
              </div>
            ))}
            {isDeploying && <div style={commonStyles.terminalLine}>_</div>} {/* Blinking cursor */}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeployExploitInterface;